import { useState, useEffect, useCallback } from 'react';
import { useSpeechSynthesis } from 'react-speech-kit';

const useTextToSpeech = () => {
    const [isSpeakActivated, setIsSpeakActivated] = useState(false);
    const [mimik, setMimik] = useState(false);
    const [selectedVoiceURI, setSelectedVoiceURI] = useState('');
    const [mimicTimeoutId, setMimicTimeoutId] = useState(null);
    const [rate, setRate] = useState(1);
    const [pitch, setPitch] = useState(1);

    const { speak, speaking, supported, voices, cancel } = useSpeechSynthesis({
        onVoicesChanged: (voices) => {
            selectDefaultVoice(voices)
        }
    });

    const selectDefaultVoice = useCallback((voices) => {
        if (voices.length > 0 && !selectedVoiceURI) {
            let defaultVoice = voices.find(voice => voice.lang.startsWith('de-')) || 
                               voices.find(voice => voice.lang.startsWith('en-'));
            if(defaultVoice){
                setSelectedVoiceURI(defaultVoice.voiceURI);
            }
        }
    }, [selectedVoiceURI]);
    
    const selectVoice = useCallback((preferredLang = 'de-DE') => {
        const voicesByLang = voices.filter(voice => voice.lang.startsWith(preferredLang));
        const localVoices = voicesByLang.filter(voice => voice.localService);
        return localVoices.length > 0 ? localVoices[0] : voicesByLang[0];
    }, [voices]);

    useEffect(() => {
        if (voices.length > 0 && !selectedVoiceURI) {
            selectDefaultVoice(voices);
        }
    }, [voices, selectedVoiceURI, selectDefaultVoice]);

    useEffect(() => {
        return () => {
            if (mimicTimeoutId) clearTimeout(mimicTimeoutId);
        };
    }, [mimicTimeoutId]);

    const isSpeaking = useCallback(() => mimik || speaking, [mimik, speaking]);

    const speakOrMimik = useCallback((text, preferredLang = 'de-DE') => {
        if (!text) {
            console.warn('No text provided to speak or mimic');
            return;
        }
        console.log('speak', text)

        if (isSpeakActivated && supported && voices.length > 0) {
            const voiceToUse = voices.find(voice => voice.voiceURI === selectedVoiceURI);
            if (voiceToUse) {
                speak({ text, voice: voiceToUse, rate, pitch });
            }
        } else {
            setMimik(true);
            const delay = text.length * 40;
            const timeoutId = setTimeout(() => setMimik(false), delay);
            setMimicTimeoutId(timeoutId);
        }
    }, [isSpeakActivated, supported, voices, selectedVoiceURI, speak]);

    const stopTalking = useCallback(() => {
        if (speaking) {
            cancel();
        }
        if (mimik) {
            setMimik(false);
            if (mimicTimeoutId) {
                clearTimeout(mimicTimeoutId);
                setMimicTimeoutId(null);
            }
        }
    }, [speaking, cancel, mimik, mimicTimeoutId]);

    const toggleSpeakActivated = useCallback(() => {
        if(isSpeakActivated){
            stopTalking();
        }
        setIsSpeakActivated(prev => !prev);
    }, [isSpeakActivated, stopTalking]);

    const getVoicesForLocale = useCallback((locale) => {
        const voicesForLocale = voices.filter(voice => voice.lang.startsWith(locale));
        return voicesForLocale.length > 0 ? voicesForLocale : voices;
    }, [voices]);

    return { supported, speakOrMimik, isSpeaking, getVoicesForLocale, isSpeakActivated, toggleSpeakActivated, 
        setSelectedVoiceURI, selectedVoiceURI, rate, pitch, setRate, setPitch, stopTalking };
};

export default useTextToSpeech;